import type { ProductQuery } from "@qw/graphql";
import {
  cn,
  EditorButtonCode,
  EditorButtonGroupHistory,
  EditorButtonGroupTextMarks,
  EditorButtonLink,
  EditorContent,
  EditorDropdownMenuChatToolName,
  EditorDropdownMenuTable,
  EditorDropdownMenuTextBlockFormat,
  EditorExtensionChatToolName,
  EditorProvider,
  EditorToolbar,
  EditorToolbarSeparator,
  FieldBase,
} from "@qw/ui";
import type { EditorEvents } from "@tiptap/core";
import "@tiptap/markdown";
import { useCurrentEditor } from "@tiptap/react";
import {
  type ComponentProps,
  type FC,
  type ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDebounceCallback } from "usehooks-ts";

import { EditorWorkflowFilesDropdownMenu } from "../../playbooks/playbook-editor/playbook-editor-workflow-files-dropdown-menu";
import { createFileUploadExtension } from "../../playbooks/playbook-editor/playbook-workflow-extensions/editor-extension-playbook-file";
import type { ProductFormValues } from "./product-form.types";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    ProductFiles: {
      insertProductFile: (fileUploadId?: string) => ReturnType;
    };
  }
}

const EditorExtensionProductFile = createFileUploadExtension({
  nodeName: "productFile",
  commandsNamespace: "ProductFiles",
  commandName: "insertProductFile",
  dataType: "product-file",
  className: "editor-extension-product-file",
});

export interface ProductWorkflowEditorToolbarProps extends ComponentProps<typeof EditorToolbar> {
  product: ProductQuery["product"];
}

const ProductWorkflowEditorToolbar: FC<ProductWorkflowEditorToolbarProps> = (props) => {
  const files = useWatch<ProductFormValues, "files">({ name: "files" });

  return (
    <EditorToolbar {...props}>
      <EditorDropdownMenuTextBlockFormat />
      <EditorToolbarSeparator />
      <EditorDropdownMenuChatToolName />
      <EditorToolbarSeparator />
      {!!files?.length && (
        <>
          <EditorWorkflowFilesDropdownMenu
            files={files}
            nodeName="productFile"
            insertCommand={(editor, id) => editor.chain().focus().insertProductFile(id).run()}
          />
          <EditorToolbarSeparator />
        </>
      )}
      <EditorButtonGroupTextMarks />
      <EditorToolbarSeparator />
      <EditorButtonLink />
      <EditorToolbarSeparator />
      <EditorButtonCode />
      <EditorDropdownMenuTable />
      <EditorToolbarSeparator />
      <EditorButtonGroupHistory />
    </EditorToolbar>
  );
};

export interface ProductWorkflowEditorProps extends ComponentProps<typeof EditorProvider> {
  product: ProductQuery["product"];
  isPaused?: boolean;
  onWorkflowMarkdownChange?: (markdown: string) => void;
}

export interface ProductWorkflowEditorHandle {
  insertContentAtCursor: (markdown: string) => void;
}

/**
 * Removes inline style attributes from pasted HTML content.
 * This ensures consistent styling when users paste content from external sources.
 */
const removeInlineStyles = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  doc.querySelectorAll("[style]").forEach((el) => el.removeAttribute("style"));
  return doc.body.innerHTML;
};

interface ProductWorkflowEditorContentProps {
  editable?: boolean;
  isPaused?: boolean;
  onWorkflowMarkdownChange?: (markdown: string) => void;
  product: ProductQuery["product"];
  workflowEditorRef: ForwardedRef<ProductWorkflowEditorHandle>;
}

const ProductWorkflowEditorContent: FC<ProductWorkflowEditorContentProps> = ({
  editable,
  isPaused,
  onWorkflowMarkdownChange,
  product,
  workflowEditorRef,
}) => {
  const { setValue } = useFormContext<ProductFormValues>();
  const { editor } = useCurrentEditor();

  useImperativeHandle(
    workflowEditorRef,
    () => ({
      insertContentAtCursor: (markdown: string) => {
        if (!editor) {
          return;
        }

        editor.chain().focus().insertContent(markdown, { contentType: "markdown" }).run();
        onWorkflowMarkdownChange?.(editor.getMarkdown());
      },
    }),
    [editor, onWorkflowMarkdownChange]
  );

  useEffect(() => {
    if (editable && editor) {
      const currentContent = editor.getHTML();
      if (!isPaused) {
        setValue("workflow", currentContent, { shouldValidate: true });
      }
      onWorkflowMarkdownChange?.(editor.getMarkdown());
    }
  }, [isPaused, editable, editor, setValue, onWorkflowMarkdownChange]);

  return (
    <FieldBase name="workflow" className={cn("flex-1", { "text-muted-foreground cursor-not-allowed": !editable })}>
      <div className={cn("flex flex-col flex-1", { "py-0": !editable })}>
        {editable && (
          <div className="sticky -top-5 border-b bg-card z-10">
            <div className="-mx-3">
              <ProductWorkflowEditorToolbar product={product} />
            </div>
          </div>
        )}
        <EditorContent className="playbook-editor max-w-4xl py-2 flex-1" />
      </div>
    </FieldBase>
  );
};

export const ProductWorkflowEditor = forwardRef<ProductWorkflowEditorHandle, ProductWorkflowEditorProps>(
  ({ editorProps, onWorkflowMarkdownChange, ...props }, ref) => {
    const { setValue } = useFormContext<ProductFormValues>();

    const handleUpdate = useDebounceCallback(
      (update: EditorEvents["update"]) => {
        if (!props.editable || props.isPaused) {
          return;
        }
        setValue("workflow", update.editor.getHTML(), { shouldValidate: true, shouldDirty: true });
      },
      250,
      { trailing: true }
    );

    const handleEditorUpdate = useCallback(
      (update: EditorEvents["update"]) => {
        if (props.editable) {
          onWorkflowMarkdownChange?.(update.editor.getMarkdown());
        }
        handleUpdate(update);
      },
      [handleUpdate, onWorkflowMarkdownChange, props.editable]
    );

    return (
      <EditorProvider
        {...props}
        placeholder="Include details like: marketing materials, appetite guide, applications required to clear/quote/bind, limits, premiums, deductibles, inclusions, exclusions, coverage highlights, and endorsements."
        extensions={[EditorExtensionChatToolName, EditorExtensionProductFile]}
        onUpdate={handleEditorUpdate}
        editorProps={{
          ...editorProps,
          transformPastedHTML: removeInlineStyles,
        }}
      >
        <ProductWorkflowEditorContent
          editable={props.editable}
          isPaused={props.isPaused}
          onWorkflowMarkdownChange={onWorkflowMarkdownChange}
          product={props.product}
          workflowEditorRef={ref}
        />
      </EditorProvider>
    );
  }
);

ProductWorkflowEditor.displayName = "ProductWorkflowEditor";
